/**
 * Filtres d'une liste d'exercices — favoris, matériel, muscle.
 *
 * 🔴 **Le même bloc de filtres des deux côtés : la bibliothèque et le sélecteur
 * d'un bloc de routine.** Deux jeux de filtres auraient fini par diverger, et
 * le symptôme aurait été celui que `shared/search.ts` existe pour empêcher —
 * la même recherche rendant des résultats différents selon l'écran d'où on la
 * lance, sans que personne ne sache pourquoi.
 *
 * ⚠️ **Les facettes se dérivent de la réplique**, jamais d'une route ni d'une
 * liste écrite ici (`exerciseFacets`, étape 4). Deux propriétés gratuites : les
 * filtres marchent **sans réseau**, et ils ne peuvent pas se désaccorder de la
 * liste qu'ils filtrent, puisqu'ils en sortent. Un vocabulaire figé afficherait
 * « Kettlebell » alors qu'aucun exercice n'en porte, et raterait le matériel
 * qu'on aura nommé soi-même.
 *
 * 🔴 **Deux rangées qui GLISSENT, jamais des puces qui s'empilent.** Le jeu
 * importé compte dix-sept muscles et une douzaine de matériels : en
 * `flex-wrap: wrap` ils occupaient la moitié de l'écran avant le premier
 * exercice — défaut vu sur la capture à l'étape 4, corrigé par
 * `.n-chip-row--scroll`, et qui se serait reproduit à l'identique en ajoutant
 * une seconde famille de filtres.
 *
 * ⚠️ **Deux rangées et pas trois** : la catégorie reste hors de l'écran, comme
 * à l'étape 4. `searchExercisesLocally` la connaît et la recherche textuelle
 * porte dessus — elle est atteignable en tapant son nom. Trois rangées de
 * puces au-dessus d'une liste, c'est un écran de formulaire, pas une
 * bibliothèque.
 *
 * ⚠️ **Le vocabulaire reste celui du jeu importé, en anglais.** Le §1.1 est
 * explicite : « la taxonomie épouse celle du jeu importé, en inventer une autre
 * imposerait une table de correspondance à tenir ». Traduire *l'affichage*
 * seulement serait pire : la recherche textuelle porte sur ces mêmes champs
 * (`searchExercisesLocally`), donc taper « pectoraux » ne trouverait rien
 * pendant que la puce afficherait « Pectoraux ».
 */

import { Button } from '../../components/nourri';
import {
  activeFilterCount,
  NO_EXERCISE_FILTER,
  type ExerciseFilterState,
} from './exerciseFilters';

/** Une puce à bascule : re-toucher la puce active la désélectionne. */
function Chip({
  label,
  active,
  onToggle,
}: {
  label: string;
  active: boolean;
  onToggle: () => void;
}) {
  return (
    <Button
      variant={active ? 'selected' : 'secondary'}
      aria-pressed={active}
      onClick={onToggle}
    >
      {label}
    </Button>
  );
}

export function ExerciseFilterBar({
  facets,
  value,
  onChange,
}: {
  facets: { equipment: string[]; muscles: string[] };
  value: ExerciseFilterState;
  onChange: (next: ExerciseFilterState) => void;
}) {
  const actifs = activeFilterCount(value);

  return (
    <>
      <div className="n-chip-row n-chip-row--scroll" role="group" aria-label="Filtres — matériel">
        <Chip
          label="★ Favoris"
          active={value.favoriteOnly}
          onToggle={() => {
            onChange({ ...value, favoriteOnly: !value.favoriteOnly });
          }}
        />
        {/*
          ⚠️ **Le bouton « Tout » n'apparaît que s'il y a quelque chose à
          effacer.** Un contrôle en permanence inerte apprend surtout à ignorer
          les contrôles ; et avec trois filtres cumulables, retrouver celui
          qu'on a laissé actif deux rangées plus loin est le geste qu'on
          cherche.
        */}
        {actifs > 0 && (
          <Button
            variant="ghost"
            onClick={() => {
              onChange(NO_EXERCISE_FILTER);
            }}
          >
            ✕ Tout
          </Button>
        )}
        {facets.equipment.map((item) => (
          <Chip
            key={item}
            label={item}
            active={value.equipment === item}
            onToggle={() => {
              onChange({ ...value, equipment: value.equipment === item ? null : item });
            }}
          />
        ))}
      </div>

      {facets.muscles.length > 0 && (
        <div className="n-chip-row n-chip-row--scroll" role="group" aria-label="Filtres — muscle">
          {facets.muscles.map((item) => (
            <Chip
              key={item}
              label={item}
              active={value.muscle === item}
              onToggle={() => {
                onChange({ ...value, muscle: value.muscle === item ? null : item });
              }}
            />
          ))}
        </div>
      )}
    </>
  );
}
